/**
 * Shared helpers for invoice Total / Due on Invoice Details (mirrors BE dealAmounts).
 */

export const INVOICE_SOURCE_DEBIT_NOTE = 'Debit Note';
export const INVOICE_SOURCE_CREDIT_NOTE = 'Credit Note';
export const INVOICE_SOURCE_EXTRA_CHARGE = 'Extra Charge';

export const ADJUSTMENT_REASON_PRICE_CORRECTION = 'price_correction';
export const ADJUSTMENT_REASON_COMMERCIAL_WRITE_OFF = 'commercial_write_off';

/** Coerce to a finite number; junk (NaN / Infinity / non-numeric) → fallback. */
export function finiteNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function isActiveInvoice(invoice) {
  return !invoice?.cancelled && invoice?.status !== 'Cancelled';
}

export function isCreditNoteInvoice(invoice) {
  return String(invoice?.source || '').trim() === INVOICE_SOURCE_CREDIT_NOTE;
}

export function isDebitNoteInvoice(invoice) {
  return String(invoice?.source || '').trim() === INVOICE_SOURCE_DEBIT_NOTE;
}

/** Late-fee invoices are additive dues, not part of the sale-price stage schedule. */
export function isLateFeeInvoice(invoice) {
  const source = String(invoice?.source || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
  return source === 'latefee' || source === 'latefees';
}

/**
 * Price-correction CN (PC/…) settles an over-billed invoice line.
 * Must NOT shrink closed-sale / Total Price (blueprint).
 */
export function isPriceCorrectionCreditNote(invoice) {
  if (!isCreditNoteInvoice(invoice)) return false;
  return String(invoice?.invoiceNumber || '')
    .trim()
    .toUpperCase()
    .startsWith('PC/');
}

/** Commercial write-off CN (WO/…) — may reduce adjusted total. */
export function isCommercialWriteOffCreditNote(invoice) {
  if (!isCreditNoteInvoice(invoice)) return false;
  return String(invoice?.invoiceNumber || '')
    .trim()
    .toUpperCase()
    .startsWith('WO/');
}

/** PC/{dealId}/{targetInvoiceId}/{ts} → target id; else infer by matching open due. */
export function getPriceCorrectionTargetInvoiceId(creditNote, invoices = []) {
  if (!isPriceCorrectionCreditNote(creditNote)) return null;
  const parts = String(creditNote?.invoiceNumber || '')
    .trim()
    .split('/');
  if (parts.length >= 4) {
    const targetId = Number(parts[2]);
    if (
      Number.isFinite(targetId) &&
      invoices.some((inv) => Number(inv?.id) === targetId)
    ) {
      return targetId;
    }
  }
  const cnAmt = finiteNumber(creditNote?.amount);
  const candidates = invoices
    .filter(
      (inv) =>
        isActiveInvoice(inv) &&
        !isCreditNoteInvoice(inv) &&
        !isLateFeeInvoice(inv)
    )
    .map((inv) => ({
      id: Number(inv.id),
      due: Math.max(
        0,
        finiteNumber(inv.amount) - finiteNumber(inv.paid_amount)
      )
    }))
    .filter((c) => c.due > 0);
  const exact = candidates.find((c) => c.due === cnAmt);
  if (exact) return exact.id;
  candidates.sort((a, b) => b.due - a.due);
  return candidates[0]?.id ?? null;
}

export function priceCorrectionAppliedToInvoice(invoiceId, invoices = []) {
  if (invoiceId == null) return 0;
  return invoices.reduce((sum, inv) => {
    if (!isActiveInvoice(inv) || !isPriceCorrectionCreditNote(inv)) return sum;
    const target = getPriceCorrectionTargetInvoiceId(inv, invoices);
    if (Number(target) !== Number(invoiceId)) return sum;
    return sum + finiteNumber(inv.amount);
  }, 0);
}

export function buildPriceCorrectionInvoiceNumber(dealId, targetInvoiceId) {
  return `PC/${dealId}/${targetInvoiceId}/${Date.now()}`;
}

export function pickPriceCorrectionTargetInvoice(invoices = []) {
  const candidates = invoices
    .filter(
      (inv) =>
        isActiveInvoice(inv) &&
        !isCreditNoteInvoice(inv) &&
        !isLateFeeInvoice(inv)
    )
    .map((inv) => ({
      invoice: inv,
      due: Math.max(
        0,
        finiteNumber(inv.amount) +
          finiteNumber(inv.late_fees) -
          finiteNumber(inv.paid_amount) -
          finiteNumber(inv.paid_late_fees)
      )
    }))
    .filter((c) => c.due > 0)
    .sort((a, b) => b.due - a.due);
  return candidates[0]?.invoice ?? null;
}

/**
 * @param {object} invoice
 * @param {object[]} [invoices] full deal set — needed so PC CNs settle the target line
 */
export function invoiceOutstandingContribution(invoice, invoices = null) {
  if (!isActiveInvoice(invoice)) return 0;
  const amount = finiteNumber(invoice?.amount);
  const late = finiteNumber(invoice?.late_fees);
  const paid = finiteNumber(invoice?.paid_amount);
  const paidLate = finiteNumber(invoice?.paid_late_fees);
  // Price-correction CN settles the target invoice line; it is not a deal-total cut.
  if (isPriceCorrectionCreditNote(invoice)) return 0;
  if (isCreditNoteInvoice(invoice)) {
    return -amount;
  }
  const raw = Math.max(0, amount + late - paid - paidLate);
  if (!Array.isArray(invoices)) return raw;
  const applied = priceCorrectionAppliedToInvoice(invoice?.id, invoices);
  return Math.max(0, raw - applied);
}

export function invoiceTotalAdjustmentContribution(invoice) {
  if (!isActiveInvoice(invoice)) return 0;
  const amount = finiteNumber(invoice?.amount);
  // Closed sale is immutable for price-correction CNs.
  if (isPriceCorrectionCreditNote(invoice)) return 0;
  if (isCreditNoteInvoice(invoice)) return -amount;
  const source = String(invoice?.source || '').trim();
  if (source === INVOICE_SOURCE_DEBIT_NOTE || source === INVOICE_SOURCE_EXTRA_CHARGE) {
    return amount;
  }
  return 0;
}

/** Active invoice principal covering the sale schedule (excludes CN + latefee rows). */
export function invoiceSaleScheduleContribution(invoice) {
  if (!isActiveInvoice(invoice)) return 0;
  if (isCreditNoteInvoice(invoice)) return 0;
  if (isLateFeeInvoice(invoice)) return 0;
  return Math.max(0, finiteNumber(invoice?.amount));
}

export function computeInvoiceOutstandingDue(invoices = []) {
  const due = invoices.reduce(
    (sum, inv) => sum + invoiceOutstandingContribution(inv, invoices),
    0
  );
  return Math.max(0, finiteNumber(due));
}

export function computeUninvoicedSaleAmount(baseTotalPrice, invoices = []) {
  const adjustedTotal = computeAdjustedDealTotal(baseTotalPrice, invoices);
  const scheduled = invoices.reduce(
    (sum, inv) => sum + invoiceSaleScheduleContribution(inv),
    0
  );
  return Math.max(0, adjustedTotal - scheduled);
}

/**
 * Deal due vs sale: unpaid active invoices + uninvoiced sale remainder − credit.
 * Pass baseTotalPrice / creditBalance when available (Invoice Details / deal list).
 */
export function computeDealDueFromInvoices(invoices = [], options = {}) {
  const invoiceDue = computeInvoiceOutstandingDue(invoices);
  const baseTotal = options?.baseTotalPrice;
  if (baseTotal == null || !Number.isFinite(Number(baseTotal))) {
    return Math.max(0, Math.round(invoiceDue));
  }
  const uninvoiced = computeUninvoicedSaleAmount(Number(baseTotal), invoices);
  const credit = Math.max(0, finiteNumber(options?.creditBalance));
  const due = invoiceDue + uninvoiced - credit;
  return Math.max(0, Math.round(finiteNumber(due)));
}

/** Alias used by Invoice Details — same as computeDealDueFromInvoices with sale context. */
export function computeDealRemainingDue(
  baseTotalPrice,
  invoices = [],
  creditBalance = 0
) {
  return computeDealDueFromInvoices(invoices, {
    baseTotalPrice,
    creditBalance
  });
}

/** Paid principal + late fees on active invoices only (excludes cancelled / credit notes). */
export function computeDealPaidFromInvoices(invoices = []) {
  return invoices.reduce((sum, inv) => {
    if (!isActiveInvoice(inv) || isCreditNoteInvoice(inv)) return sum;
    return sum + finiteNumber(inv?.paid_amount) + finiteNumber(inv?.paid_late_fees);
  }, 0);
}

export function computeAdjustedDealTotal(baseTotalPrice, invoices = []) {
  const adjustment = invoices.reduce(
    (sum, inv) => sum + invoiceTotalAdjustmentContribution(inv),
    0
  );
  return Math.max(0, Math.round(finiteNumber(baseTotalPrice) + adjustment));
}

/**
 * Sale remaining only (excludes late-fee outstanding) — used for balance write-off.
 * = uninvoiced sale + unpaid stage principal − credit
 */
export function computeSaleRemainingBalance(
  baseTotalPrice,
  invoices = [],
  creditBalance = 0
) {
  const uninvoiced = computeUninvoicedSaleAmount(baseTotalPrice, invoices);
  const unpaidPrincipal = invoices.reduce((sum, inv) => {
    if (
      !isActiveInvoice(inv) ||
      isCreditNoteInvoice(inv) ||
      isLateFeeInvoice(inv)
    ) {
      return sum;
    }
    // Line open after price-correction applications (principal only for write-off size).
    const raw = Math.max(
      0,
      finiteNumber(inv?.amount) - finiteNumber(inv?.paid_amount)
    );
    const applied = priceCorrectionAppliedToInvoice(inv?.id, invoices);
    return sum + Math.max(0, raw - applied);
  }, 0);
  const credit = Math.max(0, finiteNumber(creditBalance));
  return Math.max(0, Math.round(uninvoiced + unpaidPrincipal - credit));
}

/**
 * Outstanding on late-fee invoices only (and late_fees line on stage invoices).
 * Additive on top of sale; does not consume deal credit.
 */
export function computeLateFeeOutstandingDue(invoices = []) {
  const due = invoices.reduce((sum, inv) => {
    if (!isActiveInvoice(inv) || isCreditNoteInvoice(inv)) return sum;
    if (isLateFeeInvoice(inv)) {
      return sum + invoiceOutstandingContribution(inv, invoices);
    }
    // Stage rows can still carry an on-invoice late_fees balance
    const late = finiteNumber(inv?.late_fees);
    const paidLate = finiteNumber(inv?.paid_late_fees);
    return sum + Math.max(0, late - paidLate);
  }, 0);
  return Math.max(0, Math.round(finiteNumber(due)));
}

/** Gross stage schedule − active price-correction CNs (net billable schedule). */
export function computeNetSaleScheduleTotal(invoices = []) {
  const gross = computeSaleScheduleTotal(invoices);
  const pc = invoices.reduce((sum, inv) => {
    if (!isActiveInvoice(inv) || !isPriceCorrectionCreditNote(inv)) return sum;
    return sum + finiteNumber(inv.amount);
  }, 0);
  return Math.max(0, Math.round(gross - pc));
}

/**
 * UI breakdown so Due is not read as Total − Paid alone.
 * totalDue === saleDue + lateFeeDue (same total as computeDealRemainingDue).
 */
export function computeDealDueBreakdown(
  baseTotalPrice,
  invoices = [],
  creditBalance = 0
) {
  const saleDue = computeSaleRemainingBalance(
    baseTotalPrice,
    invoices,
    creditBalance
  );
  const lateFeeDue = computeLateFeeOutstandingDue(invoices);
  const totalDue = computeDealRemainingDue(
    baseTotalPrice,
    invoices,
    creditBalance
  );
  return {
    saleDue,
    lateFeeDue,
    totalDue,
    paid: computeDealPaidFromInvoices(invoices),
    totalPrice: computeAdjustedDealTotal(baseTotalPrice, invoices),
    closedSale: Math.round(finiteNumber(baseTotalPrice)),
    netSchedule: computeNetSaleScheduleTotal(invoices)
  };
}

export function computeSaleScheduleTotal(invoices = []) {
  return Math.max(
    0,
    Math.round(
      invoices.reduce((sum, inv) => sum + invoiceSaleScheduleContribution(inv), 0)
    )
  );
}

/** Credited − Debited from Payments-tab history rows. */
export function computeCashReceivedFromHistory(history = []) {
  let net = 0;
  for (const row of history) {
    const amt = finiteNumber(row?.amount_paid);
    const t = String(row?.type || '')
      .trim()
      .toLowerCase();
    if (t === 'debited' || t === 'debit') net -= amt;
    else net += amt;
  }
  return Math.max(0, Math.round(net));
}

/**
 * Four-balance reconcile snapshot (mirrors BE dealAmounts.computeDealReconcileSnapshot).
 */
export function computeDealReconcileSnapshot({
  baseTotalPrice,
  invoices = [],
  creditBalance = 0,
  cashReceived = 0,
  tallyLedgerBalance = null
} = {}) {
  const base = finiteNumber(baseTotalPrice);
  const credit = Math.max(0, finiteNumber(creditBalance));
  const cash = Math.max(0, Math.round(finiteNumber(cashReceived)));
  const adjusted = computeAdjustedDealTotal(base, invoices);
  const schedule = computeSaleScheduleTotal(invoices);
  const net_schedule = computeNetSaleScheduleTotal(invoices);
  // Overage vs closed sale after price-correction CNs are applied.
  const pricing_overage = Math.max(0, net_schedule - Math.round(base));
  const allocated = computeDealPaidFromInvoices(invoices);
  const open = computeDealRemainingDue(base, invoices, credit);
  const late = computeLateFeeOutstandingDue(invoices);
  const unallocated_cash = Math.max(0, cash - allocated - credit);
  const tally =
    tallyLedgerBalance == null || !Number.isFinite(Number(tallyLedgerBalance))
      ? null
      : Math.round(Number(tallyLedgerBalance));

  let status = 'matched';
  let status_label = 'Balances look consistent';
  if (pricing_overage > 0 && open > 0) {
    status = 'pricing_mismatch';
    status_label =
      'Invoice schedule exceeds closed sale — use price-correction credit note, not a new payment';
  } else if (credit > 0 && open > 0) {
    status = 'unallocated_credit';
    status_label = 'Customer credit available — apply to open invoices';
  } else if (unallocated_cash > 0 && open > 0) {
    status = 'allocation_gap';
    status_label = 'Cash received exceeds allocations — review Payments allocation';
  } else if (open > 0) {
    status = 'open_balance';
    status_label = 'Open invoice balance remains';
  } else if (tally != null && tally === 0 && open === 0) {
    status = 'matched';
    status_label = 'Matched with Tally ledger balance 0';
  }

  return {
    closed_sale: Math.round(base),
    adjusted_total: adjusted,
    schedule_total: schedule,
    net_schedule,
    pricing_overage,
    cash_received: cash,
    allocated_paid: allocated,
    open_invoice_balance: open,
    late_fee_due: late,
    credit_balance: credit,
    unallocated_cash,
    tally_ledger_balance: tally,
    status,
    status_label
  };
}

export function defaultPriceCorrectionRemarks(closedSale, scheduleTotal) {
  const sale = new Intl.NumberFormat('en-IN').format(finiteNumber(closedSale));
  const sched = new Intl.NumberFormat('en-IN').format(finiteNumber(scheduleTotal));
  return `Historical invoice schedule (₹${sched}) exceeded closed sale (₹${sale}). Price-correction credit note — not a new payment.`;
}

/** Amount shown in the table "Late Fee" column for one invoice. */
export function invoiceLateFeeColumnAmount(invoice) {
  if (!isActiveInvoice(invoice) || isCreditNoteInvoice(invoice)) return 0;
  if (isLateFeeInvoice(invoice)) {
    // Late-fee invoices store the fee in `amount`, not late_fees
    return Math.max(
      0,
      finiteNumber(invoice?.amount) +
        finiteNumber(invoice?.late_fees) -
        finiteNumber(invoice?.paid_amount) -
        finiteNumber(invoice?.paid_late_fees)
    );
  }
  return Math.max(
    0,
    finiteNumber(invoice?.late_fees) - finiteNumber(invoice?.paid_late_fees)
  );
}

/** Safe rupee label — never "₹NaN". */
export function formatInrAmount(value) {
  const n = finiteNumber(value, 0);
  return `₹${new Intl.NumberFormat('en-IN').format(n)}`;
}
